<template>
    <Draggable
        v-model="certificationSectionState"
        :group="{name: 'certifications', put: true, pull: true}"
        class="profile-certifications__list"
        draggable=".draggable-certification"
        handle=".draggable-handle"
        tag="ul"
        @end="dragging = false"
        @start="dragging = true"
        @update="dragged"
    >
        <CertificationFragment
            v-for="element in certificationSectionState"
            :key="`certification--${element.index}`"
            :is-edit-mode="isEditionEnabled"
            :is-sample="false"
            :model-value="element"
            :remove-dialog-ref="removeDialog"
            @edit="(index) => openCertificationsEditDialog(index)"
            @remove="(id) => (certificationToRemoveId = id)"
        />
    </Draggable>
    <VJoyDialog
        id="removalCertificationConfirmDialog"
        ref="removeDialog"
        :cancel-text="t('general.cancel')"
        :confirm-text="t('general.continue')"
        pattern-confirm
        @dialog:confirm="removeCertification"
    >
        <template #dialog-header>
            <p>{{ $t('generic.form.confirm.text') }}</p>
        </template>
    </VJoyDialog>
</template>

<script lang="ts" setup>
    import {useTemplateRef} from 'vue';
    import {storeToRefs} from 'pinia';
    import {VueDraggable as Draggable} from 'vue-draggable-plus';
    import {ref, useProfilePageViewMode, useTranslation} from '#imports';
    import {VJoyDialog} from '@maltjoy/core-vue';
    import CertificationFragment from '~/components/main/certifications/CertificationFragment.vue';
    import {eventBus} from '~/config/events';
    import {EditionOpenedEntryPoint} from '~/config/TrackingEvents';
    import {useCertificationSectionState} from '~/features/certifications/composables/useCertificationSectionState';
    import {useProfileStore} from '~/store/profileStore';
    import {certificationMapper} from '~/types/infra/CertificationMapper';

    // Composables
    const {t} = useTranslation();
    const {isEditionEnabled} = useProfilePageViewMode();

    // State
    const certificationSectionState = useCertificationSectionState();

    // Stores
    const {updateCertifications} = useProfileStore();
    const {certifications} = storeToRefs(useProfileStore());

    // Refs
    const removeDialog = useTemplateRef('removeDialog');
    const certificationToRemoveId = ref('');
    const dragging = ref<boolean>(false);

    async function removeCertification() {
        const currentCertifications = certifications.value?.items.filter((certification) => certification.id !== certificationToRemoveId.value) || [];

        const certificationRequests = currentCertifications.map((certification, index) => {
            return certificationMapper.toRequest(certification, index);
        });
        if (certificationRequests) {
            await updateCertifications({certifications: certificationRequests});
            removeDialog.value?.hide();
        }
    }

    function openCertificationsEditDialog(certificationId: number) {
        if (isEditionEnabled.value) {
            eventBus.emit('certifications:edit-mode', {certificationId, entryPoint: EditionOpenedEntryPoint.PROFILE_PAGE});
        }
    }

    async function dragged() {
        if (certificationSectionState.value.length > 0) {
            const displayedCertifications = certificationSectionState.value;
            const displayedCertificationIds = new Set(displayedCertifications.map((cert) => cert.id));
            const remainingCertifications =
                certifications.value?.items
                    .map((certification, index) => certificationMapper.toRequest(certification, index))
                    .filter((cert) => !displayedCertificationIds.has(cert.id)) ?? [];
            const updateCertificationsPayload = [...displayedCertifications, ...remainingCertifications];

            await updateCertifications({certifications: updateCertificationsPayload});
        }
    }
</script>

<style lang="scss" scoped>
    .profile-certifications__list {
        list-style-type: none;
    }
</style>
